import Bugsnag from '@bugsnag/js';
import type { BreadcrumbType, Event as BugsnagEvent, NotifiableError, OnErrorCallback } from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import React from 'react';
import { DateTime } from 'luxon';

import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { subscriptionsSelectors } from 'app-components/flux/subscriptions';
import { transactionsSelectors } from 'app-components/flux/transactions';
import {
  getBuildConfig,
  getClientConfig,
  getLogger,
  localPreferences,
  platform,
  tracker,
  isDemoMode,
} from 'app-components/utils/core';
import store from 'app-components/utils/redux-store';

import { navigate } from 'NavigateSetter';

const logger = getLogger('utils/core/bugsnag');

// monkey patch ResizeObserver to capture elements which caused 'ResizeObserver loop completed with undelivered notifications.' error
type PatchedResizeObserver = ResizeObserver & {
  syntheticError?: Error;
  elements?: Element[];
  timestamp?: DateTime;
};
let lastObserver: PatchedResizeObserver | undefined = undefined;
const resizeObserverSyntheticError = 'ResizeObserver snapshot';
(function () {
  if ('ResizeObserver' in window) {
    const OriginalResizeObserver = window.ResizeObserver;
    window.ResizeObserver = class ResizeObserver extends OriginalResizeObserver {
      syntheticError?: Error;
      elements?: Element[];
      timestamp?: DateTime;
      rafId: number | null = null;

      constructor(callback: ResizeObserverCallback) {
        const wrappedCallback: ResizeObserverCallback = (entries: ResizeObserverEntry[], observer: ResizeObserver) => {
          // 02/04/2026 temporary hack https://github.com/embedpdf/embed-pdf-viewer/issues/362
          // callback(entries, observer);
          if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId);
          }
          this.rafId = requestAnimationFrame(() => {
            this.rafId = null;
            // Filter out entries whose targets have been removed from the DOM.
            // Deferring via rAF means the element may have unmounted between the
            // native observer firing and this callback executing (e.g. MUI
            // TextareaAutosize calling getComputedStyle on a detached node).
            const connectedEntries = entries.filter(
              (entry) => entry?.target instanceof Element && entry.target.isConnected,
            );
            if (connectedEntries.length === 0) {
              return;
            }
            callback(connectedEntries, observer);
          });

          try {
            lastObserver = observer;
            this.elements = entries?.map((e) => e.target) || [];
            this.timestamp = DateTime.now();
          } catch (e) {
            verify(false, e);
          }
        };
        super(wrappedCallback);

        this.syntheticError = new Error(resizeObserverSyntheticError); // snapshot stacktrace
      }

      disconnect(): void {
        if (this.rafId !== null) {
          cancelAnimationFrame(this.rafId);
          this.rafId = null;
        }
        super.disconnect();
      }
    };
  }
})();

declare global {
  interface Window {
    bugsnag: typeof Bugsnag;
  }
}

export const errorsBlackList = [
  // send error to bugsnag, but suppress any other handling - errorClass:errorMessage
  /NetworkError:.*/,
  /Error:Network Error/,
  /HTTPError:.*/,
  /AxiosError:.*/,
  /LaunchDarklyFlagFetchError:.*/,
  /Assert:.*/,
  /Error:valid auth tokens not found locally/,
];

export const treatAsWarning = [
  // send error to bugsnag and popup in dev build, but don't show error page + don't track crash in Mixoanel - errorClass:errorMessage
  'ReferenceError:grecaptcha is not defined',
  'TypeError:can\'t redefine non-configurable property "userAgent"', // some firefox extension specific error
  'TypeError:Cannot redefine property: userAgent', // some chrome extension specific error
  /InvalidError:.*/, // another flavor of unhandled rejection
  /UnhandledRejection:.*/,
  /TypeError:can't access dead object/, // firefox addons error - https://udn.realityripple.com/docs/Web/JavaScript/Reference/Errors/Dead_object
  'Error:ResizeObserver loop completed with undelivered notifications.',
  "NotFoundError:Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node.",
];

const disableBugsnag = localPreferences.getDisableBugsnag();

let errorAttempts = 0;

let gUserId: string | undefined = undefined;
let gDatasetId: string | undefined = undefined;
export const bugsnagIdentify = (userId?: string, datasetId?: string) => {
  gUserId = userId;
  gDatasetId = datasetId;
};

let showErrorPageTimer: NodeJS.Timeout;

export const dismissErrorPage = () => clearTimeout(showErrorPageTimer);

export const bugsnagNotify = (
  error: NotifiableError,
  onError?: OnErrorCallback,
  postReportCallback?: (err: any, event: BugsnagEvent) => void,
) => {
  if (!disableBugsnag) {
    Bugsnag.notify(error, onError, postReportCallback);
    dismissErrorPage();
  }
};

const datasetTierNames: Record<'ACME_LITE' | 'ACME_BASIC' | 'ACME_HAB' | 'UNKNOWN', string> = {
  ACME_LITE: 'Simplifi Lite',
  ACME_BASIC: 'Simplifi',
  ACME_HAB: 'Simplifi Business And Personal',
  UNKNOWN: 'No Tier',
};

if (!disableBugsnag) {
  Bugsnag.start({
    apiKey: getBuildConfig().bugsnag_api_key, // eslint-disable-line camelcase
    appType: `${datasetTierNames['UNKNOWN']}${isDemoMode() ? ' Demo' : ''}`,
    appVersion: getClientConfig().app_version,
    autoDetectErrors: true,
    autoTrackSessions: true,
    collectUserIp: true,
    enabledBreadcrumbTypes: [
      'error',
      'navigation',
      'request',
      'user',
      // 'log', // sorry no logs until sanitize them
    ],
    logger,
    maxBreadcrumbs: 40,
    maxEvents: 2, // sometimes we need pairs assert + crash
    releaseStage: getClientConfig().environment,
    reportUnhandledPromiseRejectionsAsHandled: true,
    metadata: {
      device: {
        device: platform.device.type,
        memoryDevice:
          'deviceMemory' in navigator && typeof navigator.deviceMemory === 'number'
            ? navigator.deviceMemory * 1024 ** 3
            : undefined,
        hostname: window?.location?.hostname,
      },
    },
    plugins: [new BugsnagPluginReact(React)],
    enabledErrorTypes: {
      unhandledExceptions: true,
      unhandledRejections: false, // we introduce lint rule to catch unhandled rejections (promise/catch-or-return)
    },
    onError: (event) => {
      const sendReport = !localPreferences.getIgnoreBugsnagReport();
      if (event.originalError) {
        console.error(event.originalError); // eslint-disable-line no-console
      }

      const newEvent = event;

      if (event.originalError === 'ResizeObserver loop completed with undelivered notifications.') {
        if (lastObserver?.syntheticError) {
          bugsnagNotify(lastObserver.syntheticError);
        }
      }
      if (event.originalError.message === resizeObserverSyntheticError) {
        const elements = lastObserver?.elements?.map((e) => ({
          timestamp: lastObserver?.timestamp?.toISO(),
          timeBeforeMs: lastObserver?.timestamp
            ? DateTime.now().toMillis() - lastObserver.timestamp.toMillis()
            : undefined,
          className: e.className,
          clientLeft: e.clientLeft,
          clientTop: e.clientTop,
          clientHeight: e.clientHeight,
          clientWidth: e.clientWidth,
          scrollHeight: e.scrollHeight,
          scrollWidth: e.scrollWidth,
          scrollLeft: e.scrollLeft,
          scrollTop: e.scrollTop,
          id: e.id,
          role: e.role,
          innerHTML: e.innerHTML,
          innerText: e instanceof HTMLElement ? e.innerText : undefined,
          outerHTML: e.outerHTML,
          outerText: e instanceof HTMLElement ? e.outerText : undefined,
          textContent: e.textContent,
          localName: e.localName,
          nodeName: e.nodeName,
          name: e instanceof HTMLElement ? e.getAttribute('name') : undefined,
          tagName: e.tagName,
          title: e instanceof HTMLElement ? e.title : undefined,
          type: e instanceof HTMLInputElement ? e.type : undefined,
          value: e instanceof HTMLInputElement || e instanceof HTMLTextAreaElement ? e.value : undefined,
        }));
        newEvent.addMetadata('custom', { elements });
        newEvent.severity = 'warning';
        newEvent.unhandled = false;
      }

      newEvent.setUser(gUserId);

      const datasetTier: 'ACME_LITE' | 'ACME_BASIC' | 'ACME_HAB' | undefined = store
        ? (entitlementsSelectors.getTopTierEntitlement(store.getState())?.id as any)
        : undefined;
      const userTier = store ? subscriptionsSelectors.getActiveSubscription(store.getState())?.tierUriName : undefined;
      newEvent.addMetadata('user', { datasetId: gDatasetId, datasetTier, userTier });

      newEvent.app.type = `${datasetTierNames[datasetTier || 'UNKNOWN']}${isDemoMode() ? ' Demo' : ''}`;

      const memoryLimit = (window?.performance as any)?.memory?.jsHeapSizeLimit;
      const memoryUsed = (window?.performance as any)?.memory?.usedJSHeapSize;
      const memoryAvailable =
        typeof memoryLimit === 'number' && typeof memoryUsed === 'number' ? memoryLimit - memoryUsed : undefined;
      const transactions = store ? transactionsSelectors.getResourcesById(store.getState())?.size : null;
      newEvent.addMetadata('device', { memoryUsed, memoryAvailable, memoryLimit, transactions });

      const error = event?.errors?.[0];
      const { errorClass, errorMessage, stacktrace, type } = error;

      const errorSource = stacktrace?.[0] || event.originalError;
      const errorDetails = JSON.stringify(errorSource);
      const props = {
        href: window.location.href,
        path: window.location.pathname,
        exception_type: type,
        exception_class: errorClass,
        exception_name: errorMessage,
        exception_reason: errorDetails,
      };
      tracker.track(tracker.events.exception, props);

      const context = `${errorClass}:${errorMessage}`;
      if (!sendReport) {
        logger.warn('send crash report is suppressed from settings - report not gonna be sent...');
      }
      if (errorsBlackList.find((mask) => (typeof mask === 'string' ? context.includes(mask) : mask.test?.(context)))) {
        logger.warn(`error blacklisted: '${context}`, newEvent);
        newEvent.severity = 'warning';
        newEvent.unhandled = false;
      } else {
        logger.warn(`error will be sent to bugsnag: '${context}`, newEvent);
        let showErrorPage =
          !treatAsWarning.find((mask) => (typeof mask === 'string' ? context.includes(mask) : mask.test?.(context))) &&
          newEvent.unhandled &&
          (!errorSource?.file ||
            errorSource?.file?.startsWith(window.location.origin) ||
            errorSource?.file?.startsWith('webpack-internal://'));
        if (!showErrorPage) {
          logger.warn(`error ignored: '${context}`);
          newEvent.severity = 'warning';
          newEvent.unhandled = false;
        }

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
        const suppressWindowError = localPreferences.getSuppressWindowError();
        if (!suppressWindowError) {
          // eslint-disable-next-line no-alert
          alert(`Send Error Report to Bugsnag:\
          \nUse 'dev menu > Crash > Ignore Bugsnag Reports' to disable popups\
          \n\n${event.originalError || error}`);
          showErrorPage = false;
        }
/////////////////

        if (showErrorPage) {
          clearTimeout(showErrorPageTimer);
          showErrorPageTimer = setTimeout(() => {
            tracker.track(tracker.events.crash, props);
            setTimeout(() => {
              if (window.location.pathname !== '/error' && !errorAttempts) {
                errorAttempts += 1;
                navigate('/error');
              }
            }, 1000);
          }, 1000);
        }
      }

      return sendReport;
    },
  });
}

typeof window !== 'undefined' && (window.bugsnag = Bugsnag);

export const bugsnagBreadcrumb = (name: string, metadata?: { [key: string]: any }, type?: BreadcrumbType) => {
  if (!disableBugsnag) {
    Bugsnag.leaveBreadcrumb(name, metadata, type);
  }
};

export const BugsnagErrorBoundary = !disableBugsnag && Bugsnag.getPlugin('react');

export default !disableBugsnag && Bugsnag;
